import { Link, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import useVirtualWorld from "../../hooks/useVirtualWorld";
import vw from "../../api/virtualWorld.api";
import Loading from "../../components/Loading";
import SponsorMark, { JerseyPreview } from "../../components/VirtualWorld/SponsorMark";

const ManagerClubPage = () => {
  const { userId, manager, loading, refresh } = useVirtualWorld();
  const navigate = useNavigate();
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");
  const [placement, setPlacement] = useState("");

  const club = manager?.club;
  const offers = manager?.sponsorOffers || [];
  const outfitterOffers = manager?.outfitterOffers || [];
  const placements = manager?.sponsorPlacements || [];
  const activePlacement = placement || club?.sponsorPlacement || "board";
  const outfitter = club?.outfitter || club?.partnerships?.outfitter;

  const previewFor = useMemo(() => {
    const place = placements.find((p) => p.key === activePlacement) || { multiplier: 1 };
    const mult = Number(place.multiplier) || 1;
    return (offer) => ({
      season: Math.round((Number(offer.basePayment) || 0) * mult),
      bonus: Math.round((Number(offer.conditionReward) || 0) * mult),
      perMd: Math.round(((Number(offer.basePayment) || 0) * mult) / 14),
    });
  }, [activePlacement, placements]);

  if (loading && !manager) return <Loading />;
  if (!club) return <p>Bitte zuerst einen Club gründen.</p>;
  const money = (n) => `${Number(n || 0).toLocaleString("de-DE")} €`;
  const guided = Boolean(manager?.guided ?? manager?.progression?.manager?.guided);
  const navAllow = new Set(
    manager?.guide?.navFeatures || manager?.progression?.manager?.guide?.navFeatures || []
  );
  const linkOk = (feature) => !guided || !navAllow.size || navAllow.has(feature);

  const pick = async (key) => {
    setBusy(key);
    setMsg("");
    try {
      await vw.chooseSponsor(userId, key, activePlacement);
      const home = await refresh();
      setMsg("Sponsor & Platzierung übernommen. Gilt ab dem nächsten Spieltag.");
      // Sponsor erledigt den Guide-Schritt „Geschäftsstelle“ — weiter zum nächsten Ziel
      const next =
        home?.guide?.current ||
        home?.progression?.manager?.guide?.current ||
        null;
      if (guided && next?.to && next.key !== "club") {
        navigate(next.to);
      }
    } catch (err) {
      setMsg(err?.data?.message || "Sponsorwechsel fehlgeschlagen.");
    } finally {
      setBusy("");
    }
  };

  const pickOutfitter = async (key) => {
    setBusy(`kit:${key}`);
    setMsg("");
    try {
      await vw.chooseOutfitter(userId, key);
      await refresh();
      setMsg("Ausrüster übernommen — Material und Trainingsbonus gelten ab dem nächsten Spieltag.");
    } catch (err) {
      setMsg(err?.data?.message || "Ausrüster-Wahl fehlgeschlagen.");
    } finally {
      setBusy("");
    }
  };

  const brand = club.sponsor?.brand || null;
  const displayName = club.displayName || club.name;

  return (
    <div className="max-w-3xl space-y-6">
      <div className="bg-white dark:bg-gray-700 rounded-lg p-6 shadow space-y-3">
        <div className="flex items-start gap-4">
          {brand ? <SponsorMark brand={brand} size={56} /> : null}
          <div className="flex-1">
            <h2 className="text-2xl font-bold">🏢 {displayName}</h2>
            <p>Kürzel: {club.shortName}</p>
            <p>Reputation: {club.reputation}</p>
            <p>Budget: {money(club.budget)}</p>
            <p>Gehälter / Spieltag: {club.wageBill}</p>
          </div>
          {brand && (club.sponsorPlacement === "jersey" || club.sponsorPlacement === "sleeve" || club.sponsorPlacement === "name") ? (
            <JerseyPreview brand={brand} placement={club.sponsorPlacement} />
          ) : null}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-700 rounded-lg p-6 shadow space-y-3">
        <h3 className="font-bold text-xl">Partner-Setup (wie im echten Darts)</h3>
        <p className="text-sm opacity-80">
          <strong>Ausrüster</strong> liefert Material und stärkt den Trainingsraum.
          <strong> Hauptsponsor</strong> zahlt die Saisonrate auf dem Trikot.
          Zweiter Trikot-Partner und Turnier-Veranstalter folgen als Nächstes.
        </p>
        <div className="grid sm:grid-cols-2 gap-2 text-sm">
          <div className="rounded-xl border p-3">
            <div className="text-xs uppercase opacity-60">Ausrüster</div>
            <div className="font-bold">{outfitter?.chosen ? outfitter.name : "noch offen"}</div>
            {outfitter?.badge ? <div className="text-xs opacity-70">Badge: {outfitter.badge}</div> : null}
          </div>
          <div className="rounded-xl border p-3">
            <div className="text-xs uppercase opacity-60">Hauptsponsor</div>
            <div className="font-bold">{club.sponsorChosen ? club.sponsorName : "noch offen"}</div>
          </div>
          <div className="rounded-xl border p-3 opacity-60">
            <div className="text-xs uppercase">2. Trikot-Partner</div>
            <div className="font-bold">Demnächst</div>
          </div>
          <div className="rounded-xl border p-3 opacity-60">
            <div className="text-xs uppercase">Turnier-Veranstalter</div>
            <div className="font-bold">Demnächst</div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-700 rounded-lg p-6 shadow">
        <h3 className="font-bold text-xl mb-2">🎯 Ausrüster (Kit)</h3>
        <p className="text-sm opacity-80 mb-4">
          Versorgt euch mit Material (Gutschrift pro Spieltag), verbessert das Training und gibt Rabatt
          auf Board-/Trainings-Upgrades in der Heimstätte. Badge landet in der Ruhmeshalle.
        </p>
        <p className="mb-4 font-semibold">
          Aktuell: {outfitter?.chosen ? outfitter.name : "noch keiner"}
          {outfitter?.materialPerMatchday
            ? ` · ~${money(outfitter.materialPerMatchday)} Material/Spieltag`
            : ""}
          {outfitter?.trainingForm ? ` · Training +${outfitter.trainingForm}` : ""}
        </p>
        <div className="grid md:grid-cols-2 gap-4 mb-2">
          {outfitterOffers.map((o) => {
            const current = outfitter?.key === o.key;
            return (
              <button
                key={o.key}
                type="button"
                disabled={Boolean(busy) || current}
                className={`p-4 border rounded-xl text-left ${
                  current ? "border-emerald-400 bg-emerald-50 dark:bg-gray-800" : "hover:border-emerald-400"
                }`}
                onClick={() => pickOutfitter(o.key)}
              >
                <div className="flex gap-3 items-start">
                  <SponsorMark brand={o.brand} size={48} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase opacity-60">{o.tag || o.key}</div>
                    <div className="font-bold">{o.name}</div>
                    <div className="text-sm font-semibold mt-1">Badge: {o.badge}</div>
                    <div className="text-xs opacity-80 mt-1">
                      Material ~{money(o.materialPerMatchday)}/Spieltag · Training +{o.trainingForm} ·
                      Boards −{Math.round((Number(o.upgradeDiscount) || 0) * 100)} %
                      {Number(o.reputationBonus) > 0 ? ` · Reputation +${o.reputationBonus}` : ""}
                    </div>
                    <div className="text-sm opacity-70 mt-2">{o.blurb}</div>
                    <div className="text-sm mt-2 font-semibold">
                      {current ? "Aktiv" : busy === `kit:${o.key}` ? "…" : "Ausrüster wählen"}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-700 rounded-lg p-6 shadow">
        <h3 className="font-bold text-xl mb-2">🤝 Hauptsponsor (Trikot / Geld)</h3>
        <p className="text-sm opacity-80 mb-4">
          Wähle Marke und Sichtbarkeit. Mehr Präsenz auf Trikot oder im Clubnamen = mehr Geld —
          dafür sieht jeder den Partner in Tabelle und Match.
        </p>
        <p className="mb-4 font-semibold">
          Aktuell: {club.sponsorName || "noch keiner"}
          {club.sponsorPlacement ? ` · ${placements.find((p) => p.key === club.sponsorPlacement)?.label || club.sponsorPlacement}` : ""}
        </p>

        <div className="mb-5">
          <div className="text-xs uppercase tracking-wide opacity-60 mb-2">Sichtbarkeit / Geldstufe</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {placements.map((p) => {
              const active = activePlacement === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPlacement(p.key)}
                  className={`text-left p-3 rounded-xl border ${
                    active ? "border-amber-400 bg-amber-50 dark:bg-gray-800" : "hover:border-amber-300"
                  }`}
                >
                  <div className="font-bold flex justify-between gap-2">
                    <span>{p.label}</span>
                    <span className="text-emerald-700 dark:text-emerald-300">×{Number(p.multiplier).toFixed(2)}</span>
                  </div>
                  <div className="text-xs opacity-70 mt-1">{p.blurb}</div>
                </button>
              );
            })}
          </div>
        </div>

        {msg ? <p className="mb-4 font-semibold">{msg}</p> : null}
        <div className="grid md:grid-cols-2 gap-4">
          {offers.map((s) => {
            const current = club.sponsorKey === s.key && (club.sponsorPlacement || "board") === activePlacement;
            const prev = previewFor(s);
            return (
              <button
                key={s.key}
                disabled={Boolean(busy) || current}
                className={`p-4 border rounded-xl text-left ${
                  current ? "border-yellow-400 bg-yellow-50 dark:bg-gray-800" : "hover:border-yellow-400"
                }`}
                onClick={() => pick(s.key)}
              >
                <div className="flex gap-3 items-start">
                  <SponsorMark brand={s.brand} size={48} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase opacity-60">{s.tag || s.key}</div>
                    <div className="font-bold">{s.name}</div>
                    <div className="font-black text-lg">{money(prev.season)}</div>
                    {prev.bonus ? <div className="text-sm text-emerald-700 dark:text-emerald-300">+ {money(prev.bonus)} Bonus</div> : null}
                    <div className="text-xs opacity-70 mt-1">~{money(prev.perMd)} / Spieltag</div>
                    <div className="text-sm opacity-70 mt-2">{s.blurb}</div>
                    <div className="text-sm mt-2 font-semibold">
                      {current ? "Aktiv" : busy === s.key ? "…" : "Übernehmen"}
                    </div>
                  </div>
                  <JerseyPreview brand={s.brand} placement={activePlacement} className="hidden sm:block" />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-700 rounded-lg p-6 shadow">
        <h3 className="font-bold text-xl mb-2">Verträge</h3>
        <p className="text-sm opacity-80 mb-3">
          {guided && !linkOk("transfers")
            ? "Kündigen und Verlängern läuft über die Mannschaft. Transfermarkt kommt später in der Begleitung."
            : "Kündigen, verlängern und neue Spieler holen läuft über Team und Transfermarkt."}
        </p>
        <div className="flex flex-wrap gap-3">
          <Link className="px-4 py-2 bg-gray-900 text-white rounded" to="/manager/team">
            Zum Team
          </Link>
          {linkOk("venue") ? (
            <Link className="px-4 py-2 bg-gray-900 text-white rounded" to="/manager/venue">
              Zur Heimstätte
            </Link>
          ) : null}
          {linkOk("transfers") ? (
            <Link className="px-4 py-2 bg-gray-900 text-white rounded" to="/manager/transfers">
              Zum Transfermarkt
            </Link>
          ) : null}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-700 rounded-lg p-6 shadow">
        <h3 className="font-bold pt-1 mb-2">Letzte Buchungen</h3>
        <ul className="text-sm space-y-1">
          {(manager.finances || []).map((f) => (
            <li key={f._id}>
              {f.note} ({f.amount})
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-2xl border border-red-300 bg-red-50 dark:bg-red-950/30 p-6 space-y-3">
        <h3 className="font-bold text-xl">Club abgeben / Insolvenz</h3>
        <p className="text-sm opacity-80">
          Du steigst aus. Der Verein läuft als AI weiter (Liga bleibt intakt). Danach kannst du einen neuen Club
          gründen — mit weniger Startkapital.
        </p>
        {manager.clubExit ? (
          <ul className="text-sm space-y-1">
            <li>
              Abgabe → nächstes Startkapital{" "}
              <strong>{money(manager.clubExit.nextHandOverBudget)}</strong>
              {manager.clubExit.fullStartBudget
                ? ` (statt ${money(manager.clubExit.fullStartBudget)})`
                : ""}
            </li>
            <li>
              Insolvenz → nächstes Startkapital{" "}
              <strong>{money(manager.clubExit.nextInsolvencyBudget)}</strong>
              {!manager.clubExit.canInsolvency
                ? " · erst bei knapper Kasse oder Bankschuld wählbar"
                : ""}
            </li>
            {manager.clubExit.exits > 0 ? (
              <li className="opacity-70">Bereits {manager.clubExit.exits}× ausgestiegen (Kapital sinkt weiter).</li>
            ) : null}
          </ul>
        ) : null}
        <div className="flex flex-wrap gap-3 pt-1">
          <button
            type="button"
            disabled={Boolean(busy)}
            className="px-4 py-2 rounded-xl bg-gray-900 text-white font-semibold disabled:opacity-50"
            onClick={async () => {
              if (
                !window.confirm(
                  `Club wirklich abgeben?\n${club.name} wird AI.\nNeues Startkapital ca. ${money(
                    manager.clubExit?.nextHandOverBudget
                  )}.`
                )
              ) {
                return;
              }
              setBusy("hand_over");
              setMsg("");
              try {
                const res = await vw.abandonClub(userId, "hand_over");
                await refresh();
                navigate("/manager", {
                  replace: true,
                  state: { flash: res.data?.message || "Club abgegeben." },
                });
              } catch (err) {
                setMsg(err?.data?.message || "Abgabe fehlgeschlagen.");
              } finally {
                setBusy("");
              }
            }}
          >
            Club abgeben
          </button>
          <button
            type="button"
            disabled={Boolean(busy) || manager.clubExit?.canInsolvency === false}
            className="px-4 py-2 rounded-xl bg-red-700 text-white font-semibold disabled:opacity-40"
            title={
              manager.clubExit?.canInsolvency === false
                ? "Nur bei knapper Kasse oder offener Bankschuld"
                : "Insolvenz anmelden"
            }
            onClick={async () => {
              if (
                !window.confirm(
                  `Insolvenz für ${club.name}?\nAI übernimmt.\nNeues Startkapital ca. ${money(
                    manager.clubExit?.nextInsolvencyBudget
                  )} (stärker gekürzt).`
                )
              ) {
                return;
              }
              setBusy("insolvency");
              setMsg("");
              try {
                const res = await vw.abandonClub(userId, "insolvency");
                await refresh();
                navigate("/manager", {
                  replace: true,
                  state: { flash: res.data?.message || "Insolvenz gemeldet." },
                });
              } catch (err) {
                setMsg(err?.data?.message || "Insolvenz fehlgeschlagen.");
              } finally {
                setBusy("");
              }
            }}
          >
            Insolvenz
          </button>
        </div>
        {msg ? <p className="text-sm font-semibold">{msg}</p> : null}
      </div>
    </div>
  );
};

export default ManagerClubPage;
